import { PacketConfig } from 'app/config/config.service';
import { Asset, IMS_TYPE__ASSET } from 'app/shared/collections/asset';
import { AssetCollection } from 'app/shared/collections/asset_collection.service';
import { ImsObject } from 'app/shared/collections/ims_object';
import { IpDevice } from 'app/shared/collections/ip_device';
import { Radio } from 'app/shared/collections/radio';
import {
    FieldSchemaUnits,
    STAT_DEFINITION__PARENT_TYPE_ASSET,
    STAT_DEFINITION__PARENT_TYPE_IP_DEVICE,
    STAT_DEFINITION__PARENT_TYPE_RADIO,
    STAT_DEFINITION__PARENT_TYPE_ZONE,
    StatDefinition,
} from 'app/shared/collections/StatDefinition';
import { ImsTimeCollection } from 'app/shared/collections/time/ims_time_collection.factory';
import {
    ViewFollowRelationshipConfig,
    ViewInfoboxFollowRelationship,
} from 'app/shared/collections/view';
import { ImsItemPanelService } from 'app/shared/ims_items/ImsItemPanelService';
import { ImsItemTypeRegistry } from 'app/shared/ims_items/ImsItemTypeRegistry';
import { StatFormatterForInfoBox } from 'app/shared/info_box/components/infobox/generic_infobox_utils_stat_formatter';
import { ClockHandler } from 'app/shared/time/clock_handler.service';
import { ToastService } from 'app/shared/toast/ToastService';
import { UnitService } from 'app/shared/unit.service';
import { ViewStateService } from 'app/shared/view_state.service';
import { StatFilterFn } from 'app/views/value_getters/value_getter';
import { IpDeviceStat } from 'app/views/view_types/network/collections/ip_device_stat';
import { IMSVisualiser } from 'app/views/visualiser';
import { flatten, isFinite, isNil, isString, sortBy, uniq } from 'lodash';
import { Zone } from '../../../collections/zones/zone';
import { KMLFeatureLayer } from 'app/shared/collections/kml_feature_layer';

// This is always the name of the timestamp field that IMS uses for stat rows
export const TIMESTAMP_FIELD_NAME = 'timestamp';

export const IP_DEVICE_ID_FIELD_NAME = 'ipDeviceId';

export const JUMP_TO_TIMESTAMP = 'Jump to timestamp';

interface BaseStatEntry {
    id?: number;
    name: string;
    value:
        | string
        | number
        | { fieldName: string; imsType: string; authHeaders: any; toastService: ToastService };
    type?: string;
    // Behaviour when user clicks on the value
    onValueClickFunc?: () => void;
    // tooltip text when hovering over the value
    valueTooltipText?: string;
    imsObjectId?: number;
    imsType?: string;
    fieldName?: string;
}

export type StatEntry = BaseStatEntry;

interface StatGroup {
    name: string;
    entries: StatEntry[];
}

interface BuildStatResult {
    anyDataToShow: boolean;
    stats: StatGroup[];
    children?: Array<{
        name: string;
        groups: Array<StatGroup>;
    }>;
}

export function formatValue(
    value: number | string,
    unit: FieldSchemaUnits,
    minDecimalPlaces: number = 2,
): string {
    const converted = UnitService.convertValue(unit, value);

    if (isFinite(converted.value)) {
        const digits = UnitService.getFixed(converted.value as number, minDecimalPlaces);
        return `${(converted.value as number).toFixed(digits)}${converted.unit}`;
    } else if (isString(converted.value)) {
        return `${converted.value}${converted.unit}`;
    } else if (isNil(converted.value)) {
        return 'Unknown';
    }

    return '';
}

export function formatLocationStatValue(value: Array<number>): string {
    return `${value[1].toFixed(6)}, ${value[0].toFixed(6)}`;
}

export function buildPacketConfigStatEntries(
    packetConfigs: Array<PacketConfig>,
    stat: IpDeviceStat,
    timeZone: string,
): Array<StatEntry> {
    if (stat instanceof IpDeviceStat) {
        const formattedStat = stat.formatted(timeZone, true); // throughputAsBits shouldn't matter here
        return (packetConfigs || []).reduce((items, item) => {
            const rawValue = formattedStat[item.logicalName];
            // only include an entry if the raw value is not null, otherwise you'll get heaps of "unknowns"
            if (!isNil(rawValue)) {
                const value = formatValue(formattedStat[item.logicalName], null);
                items.push({
                    name: item.humanName,
                    value,
                });
            }
            return items;
        }, []);
    }
    return [];
}

export function getOpenPanelText(name: string): string {
    return `Open panel for ${name}`;
}

function getSubgroupField(schema: StatDefinition): string {
    for (const key of Object.keys(schema.field_schemas)) {
        if (schema.field_schemas[key].is_subgroup) {
            return key;
        }
    }

    return '';
}

function isTraverseBackwards(config: ViewFollowRelationshipConfig) {
    return config.direction == 'backwards' || config.direction == 'both';
}

function isTraverseForwards(config: ViewFollowRelationshipConfig) {
    return !config.direction || config.direction == 'forwards' || config.direction == 'both';
}

export function collectRelatedAssetsUsingConfig(
    config: ViewFollowRelationshipConfig,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    imsObj: ImsObject,
    time: Cesium.JulianDate,
) {
    const collection: ImsTimeCollection = imsItemTypeRegistry.getTypeDetails(
        config.relationship_table,
    )?.collection;

    if (!collection) {
        // don't crash if the user typo's a schema name
        return [];
    }

    const assetCollection: AssetCollection =
        imsItemTypeRegistry.getTypeDetails(IMS_TYPE__ASSET).collection;

    const assets = [];

    // if we're going in the forwards direction, that means follow the relationship
    //  stat as you'd expect from parent -> foreign key
    // default direction behaviour is forward, if none is specified
    if (isTraverseForwards(config)) {
        // So for the given ims object, find all it's current relationship stats
        //  and for each of those stats, find the related asset (foreign key field)
        //  and for each of those related assets, collect infobox data
        assets.push(
            ...collection
                .getStats(imsObj.id, time)
                .map((stat) => assetCollection.get(stat[config.relationship_field]))
                .filter((asset) => !!asset),
        );
    }

    if (isTraverseBackwards(config)) {
        // in the backwards direction, we have to ask EVERY asset, for its current list of stats
        //  and see if WE are the foreign key for any of those.
        //  it's not going to be fast, but there's probably worst places in the app
        //  we can optimize/cache this information when it proves to be an issue
        assets.push(
            ...assetCollection.getAllAsArray().reduce((assets, asset) => {
                // ask every asset for its current relationship stats
                const allStats = collection.getStats(asset.id, time);
                // check if our initial asset is the related asset
                const isAssetRelatedToAsset = allStats.find(
                    (stat) => stat[config.relationship_field] === imsObj.id,
                );
                if (isAssetRelatedToAsset) {
                    // if so, then this other asset is "one of the related ones"
                    assets.push(asset);
                }
                return assets;
            }, [] as Array<Asset>),
        );
    }
    return assets;
}

function buildGroup<T extends ImsObject>(
    stat: T,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    statFormatter: StatFormatterForInfoBox,
    timeZone: string,
): StatGroup {
    const details = imsItemTypeRegistry.getTypeDetails(stat.imsType);
    const schema = details.statDefinition;

    // check if any of the fields for this stat are a sub group
    const subgroupFieldName = getSubgroupField(schema);
    // if so, append value of the subgroup field for this stat to the group name, since we assume there'll be more than one

    const subgroupValue = subgroupFieldName ? stat[subgroupFieldName] : null;

    const name = subgroupFieldName
        ? `${schema.display_name} ${formatValue(subgroupValue, null)}`
        : schema.display_name;

    return {
        name,
        entries: statFormatter.formatStatForInfoBox(stat, schema, timeZone),
    };
}

/**
 * Build the stats for display in the infobox.
 * This is designed for use with the IMS dynamic stat system!
 *
 * @param time
 * @param {Asset | IpDevice | Zone} imsObj
 * @param {ViewStateService} viewStateService
 * @param {ImsItemTypeRegistry} imsItemTypeRegistry
 * @param {ImsItemPanelService} imsItemPanelService
 * @param clockHandler
 * @param {string} timeZone
 * @param {Function} filterFunction
 * @param {StatFormatterForInfoBox} statFormatter
 * @param statTypeFilter A list of schema/stat names to build stats for, if nil, all possible stats are built
 * @param recurse implementation detail, leave true if calling, probably
 * @param children global accumulator for all our recursed values
 * @param path separate accumulator for each recursion path to catch cycles (and prevent infinite recursion)
 * @param depth separate accumulator for each recursion path to catch cycles (and prevent infinite recursion)
 * @returns {BuildStatResult}
 */
function buildStats(
    time: Cesium.JulianDate,
    imsObj: Asset | IpDevice | Radio | Zone | KMLFeatureLayer,
    viewStateService: ViewStateService,
    imsItemTypeRegistry: ImsItemTypeRegistry,
    imsItemPanelService: ImsItemPanelService,
    clockHandler: ClockHandler,
    timeZone: string,
    statFormatter: StatFormatterForInfoBox,
    filterFunction?: StatFilterFn,
    statTypeFilter?: Array<string> | null,
    recurse = true,
    children = [],
    path: string[] = null,
    depth = 0,
): BuildStatResult {
    // Type check - we should get rid of this at some point (haha or double down)
    const itIsAnAsset = imsObj instanceof Asset;
    const itIsAnIpDevice = imsObj instanceof IpDevice;
    const itIsARadio = imsObj instanceof Radio;
    const itIsAZone = imsObj instanceof Zone;

    let visualizers: Array<IMSVisualiser> = [];

    if (itIsAnAsset || itIsAnIpDevice || itIsARadio) {
        visualizers = Object.values(viewStateService.currentViewController.assetImsVisualisers);
    } else if (itIsAZone) {
        visualizers = Object.values(viewStateService.currentViewController.zoneImsVisualisers);
    }

    const collections: Array<ImsTimeCollection> = uniq(
        visualizers
            .filter((v) => !statTypeFilter || statTypeFilter.includes(v.tableName))
            .map((visualizer) =>
                visualizer.valueGetter ? visualizer.valueGetter.statCollection : null,
            ),
    ).filter((col) => !!col);

    // TODO: bit hacky- find all dynamic stats w/ parent type of radio (to supplement the code
    // that gets collections from visualisers)
    if (itIsARadio) {
        imsItemTypeRegistry.getRegisteredStatTypes().map((statType: string) => {
            // we're only interested in dynamic stats w/ parent type of radio
            const typeDetails = imsItemTypeRegistry.getTypeDetails(statType);
            if (typeDetails?.statDefinition?.parent_type !== STAT_DEFINITION__PARENT_TYPE_RADIO) {
                return;
            }

            // handle the unexpected error
            const collection = typeDetails?.collection;
            if (!collection) {
                return;
            }

            // handle this having already been handled
            if (collections.includes(collection)) {
                return;
            }

            // find out all the tables this view should be showing
            const tables = viewStateService?.getCurrentView()?.tables;
            if (!tables) {
                return;
            }

            // make sure we're not showing something we don't want to see
            const tableNames = Object.keys(tables);
            if (!tableNames.includes(statType)) {
                return;
            }

            // add this collection to the collections we can build data for
            collections.push(collection);
        });
    }

    let imsStatObjects: Array<ImsObject> = flatten(
        collections.map((collection) => collection.getStats(Number(imsObj.id), time)),
    ).filter((stat: ImsObject) => {
        if (!stat) {
            return false;
        }
        const type = stat.imsType;
        const typeDetails = imsItemTypeRegistry.getTypeDetails(type);

        if (!typeDetails || !typeDetails.statDefinition) {
            // for something like ip device stats, they're handled manually and shouldn't have a type definition!?
            return false;
        }

        const isAssetStat =
            typeDetails.statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ASSET;

        const isIpDeviceStat =
            typeDetails.statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_IP_DEVICE;

        const isRadioStat =
            typeDetails.statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_RADIO;

        const isZoneStat =
            typeDetails.statDefinition.parent_type === STAT_DEFINITION__PARENT_TYPE_ZONE;

        // take stats if they're of the correct type!
        return (
            (isAssetStat && itIsAnAsset) ||
            (isIpDeviceStat && itIsAnIpDevice) ||
            (isRadioStat && itIsARadio) ||
            (isZoneStat && itIsAZone)
        );
    });

    // Filters the imsStatObjects by a filter function that is used to handle view component filters currently
    if (filterFunction) {
        imsStatObjects = imsStatObjects.filter(filterFunction);
    }

    // turn each stat into an info box "stat" which has a name and a list of "entries" for each column
    //  in the stat. Don't bother including any "stats" where all the columns were filtered out for whatever reason.
    // For example a stat where all the values are null won't appear in the infobox
    const outputs = imsStatObjects
        .map((stat) => buildGroup(stat, imsItemTypeRegistry, statFormatter, timeZone))
        .filter((o) => o.entries && o.entries.length > 0);

    if (recurse) {
        // the view can be configured to follow a foreign key dynamic stat
        //  and show information about the related asset
        //  either all it's normal infobox information, or just a subset of schemas
        const configs: Array<ViewInfoboxFollowRelationship> =
            viewStateService?.currentViewController?._imsView?.infoboxFollowRelationshipConfigs ||
            [];

        // in the future we may want to group children by asset, rather than having a new entry for each config and asset pair
        //  we probably just want a new entry for each asset, but there's no use case for that right now.

        for (const config of configs) {
            const relatedAssets = collectRelatedAssetsUsingConfig(
                config,
                imsItemTypeRegistry,
                imsObj as ImsObject,
                time,
            );

            const relatedThings = [];

            path = path ? path : [];

            relatedThings.push(
                ...relatedAssets
                    .map((asset) => {
                        const thisHop = `${asset?.id} -> ${imsObj?.id}`;
                        const thisHopReverse = `${imsObj?.id} -> ${asset?.id}`;

                        // avoid infinite recursion (e.g. in the case of relationship stat loops)
                        if (path.includes(thisHop) || path.includes(thisHopReverse)) {
                            return null;
                        }

                        // honour the recursion limit
                        if (config.max_depth && depth >= config.max_depth) {
                            return null;
                        }

                        path.push(thisHop);

                        // build all the stats for each related asset
                        const outputs = buildStats(
                            time,
                            asset,
                            viewStateService,
                            imsItemTypeRegistry,
                            imsItemPanelService,
                            clockHandler,
                            timeZone,
                            statFormatter,
                            filterFunction,
                            config.related_tables,
                            // TODO: this was hard-set to false (locking us to 1-hop deep); now it's hard-set
                            //  to true (forcing us to recurse forever); the comment before this one said this
                            //  should be configurable and it was absolutely right
                            true,
                            children,
                            path,
                            depth + 1,
                        );

                        return {
                            name: asset.name,
                            groups: outputs.stats,
                        };
                    })

                    // for now, don't show the related asset if there's no data to show for it.
                    //  alternatively, maybe we want to show information
                    //  about the relationship itself in the future, or the fact the relationship
                    //  exists at all is worth showing. Probably all configurable behaviour.
                    .filter((output) => output && output.groups.length > 0),
            );

            children.push(...relatedThings);
        }
    }

    return {
        anyDataToShow: outputs.length > 0,
        stats: sortBy(outputs, 'name'),
        children,
    };
}
